import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ClearanceStatus, EmployeeStatus, Role } from '@cfl/assets-shared';

import { api } from '@/lib/api';
import { toQuery } from '@/lib/query';

export interface EmployeeSummary {
  id: string;
  employeeCode: string;
  name: string;
  email: string | null;
  department: string | null;
  designation: string | null;
  phone: string | null;
  status: EmployeeStatus;
  role: Role;
  joinedAt: string | null;
  assetsHeld: number;
  /** False for an imported record with no email - they cannot sign in until HR adds
   *  one, and "why can't I log in" is otherwise invisible. */
  canSignIn: boolean;
}

export interface EmployeePage {
  employees: EmployeeSummary[];
  total: number;
  page: number;
  pageSize: number;
}

export interface EmployeeDetail extends EmployeeSummary {
  notes: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  exits: Array<{
    id: string;
    exitDate: string;
    lastWorkingDate: string | null;
    reason: string | null;
    clearanceStatus: ClearanceStatus;
    comments: string | null;
    outstandingAtClearance: number;
    recordedByName: string | null;
    createdAt: string;
  }>;
}

/** Mirrors listEmployeesSchema in the API. */
export type EmployeeSort =
  | 'name'
  | 'employeeCode'
  | 'department'
  | 'role'
  | 'status'
  | 'assetsHeld'
  | 'joinedAt';

export interface EmployeeFilters {
  search?: string;
  status?: EmployeeStatus | '';
  role?: Role | '';
  department?: string;
  holdingAssets?: boolean;
  includeExited?: boolean;
  page?: number;
  pageSize?: number;
  sort?: EmployeeSort;
  direction?: 'asc' | 'desc';
}

export const employeeKeys = {
  all: ['employees'] as const,
  list: (filters: EmployeeFilters) => ['employees', 'list', filters] as const,
  detail: (id: string) => ['employees', 'detail', id] as const,
  summary: ['employees', 'summary'] as const,
  assignable: ['employees', 'assignable'] as const,
  departments: ['employees', 'departments'] as const,
};

/**
 * `enabled` exists because the directory needs `viewRegister`, and a plain EMPLOYEE has
 * screens that optionally show it - the asset-request form's "who is it for" picker. Left
 * unconditional, those screens would fire a request that 403s for exactly the people the
 * feature is most for.
 */
export function useEmployees(filters: EmployeeFilters, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: employeeKeys.list(filters),
    queryFn: () => api.get<EmployeePage>(`/employees${toQuery(filters)}`),
    placeholderData: (previous) => previous,
    enabled: options.enabled ?? true,
  });
}

export function useEmployee(id: string | null) {
  return useQuery({
    queryKey: employeeKeys.detail(id ?? ''),
    queryFn: () => api.get<EmployeeDetail>(`/employees/${id}`),
    enabled: id !== null,
  });
}

export function useEmployeeSummary() {
  return useQuery({
    queryKey: employeeKeys.summary,
    queryFn: () =>
      api.get<{
        active: number;
        noticePeriod: number;
        inactive: number;
        exited: number;
        holdingAssets: number;
      }>('/employees/summary'),
  });
}

/** The Assign screen's picker: everyone who may legitimately receive an asset. */
export function useAssignableEmployees(enabled = true) {
  return useQuery({
    queryKey: employeeKeys.assignable,
    queryFn: () =>
      api.get<
        Array<{
          id: string;
          employeeCode: string;
          name: string;
          department: string | null;
          status: EmployeeStatus;
        }>
      >('/employees/assignable'),
    enabled,
    staleTime: 60_000,
  });
}

export function useDepartments() {
  return useQuery({
    queryKey: employeeKeys.departments,
    queryFn: () => api.get<string[]>('/employees/departments'),
    staleTime: 5 * 60_000,
  });
}

export interface EmployeeInput {
  employeeCode?: string;
  name: string;
  email?: string | null;
  department?: string | null;
  designation?: string | null;
  phone?: string | null;
  joinedAt?: string | null;
  status?: EmployeeStatus;
  role?: Role;
  notes?: string | null;
}

export function useCreateEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EmployeeInput) => api.post<EmployeeSummary>('/employees', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: employeeKeys.all }),
  });
}

export function useUpdateEmployee(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<EmployeeInput>) =>
      api.patch<EmployeeSummary>(`/employees/${id}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: employeeKeys.all }),
  });
}

/** Separate from useUpdateEmployee because the API revokes that person's sessions. */
export function useChangeRole(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { role: Role; reason?: string }) =>
      api.patch<EmployeeSummary>(`/employees/${id}/role`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: employeeKeys.all }),
  });
}

/** Separate again: it invalidates every spreadsheet quoting the old code. */
export function useChangeEmployeeCode(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { employeeCode: string }) =>
      api.patch<EmployeeSummary>(`/employees/${id}/code`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: employeeKeys.all }),
  });
}

export function useDeleteEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/employees/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: employeeKeys.all }),
  });
}
